import { useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import submit from "submit-form";
import Modal from "../../../../../parts/modal-new";
import SvgIcon from "../../../../../parts/SvgIcon";
import { getDateToDisplay } from "../../../../../utils/nutrition-helpers";
import { simpleHash } from "../../../../../utils/string-helpers";

/**
 * Function to get the date range label
 */
function getDateRangeLabel(startDate, endDate, idx) {
  if (!startDate || !endDate) return idx;
  if (startDate === endDate) return getDateToDisplay(startDate);
  return `${getDateToDisplay(startDate)} - ${getDateToDisplay(endDate)}`;
}

export const DuplicateMealPlanWeekModal = ({ locals }) => {
  const { t } = useTranslation("coaching-portal");
  const { query, mealPlan } = locals;
  const { mealPlan: mealPlanId, week = 1 } = query;
  const { weeks = [] } = mealPlan;
  const weekIndex = week - 1;
  const weekToDuplicate = weeks[weekIndex];
  const { rangeWeek } = weekToDuplicate;
  const { start: startDate = null, end: endDate = null } = rangeWeek;
  const otherWeekIndexes = weeks
    .map((_, i) => i)
    .filter((i) => i !== weekIndex);

  const dateToUse = getDateRangeLabel(startDate, endDate, week);

  const [selectedWeekIndexes, setSelectedWeekIndexes] = useState([]);

  /**
   * Function to select the weeks
   * @param {*} index
   */
  const toggleWeekSelection = (index) => {
    setSelectedWeekIndexes((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  /**
   * Duplicate action
   */
  const handleDuplicate = () => {
    const payload = {
      weekToCopy: weekIndex,
      weeksDestination: [...selectedWeekIndexes].sort((a, b) => a - b),
      isTemplate: !(startDate && endDate),
    };

    const requestId = simpleHash(JSON.stringify(payload));

    const body = {
      ...payload,
      requestId,
    };

    submit(
      `/actions/meal-planner/duplicate-week-to-weeks-meal-plan?mealPlanId=${mealPlanId}`,
      {
        method: "POST",
        body,
      }
    );
  };

  /**
   * Reset the state
   */
  const handleClearAll = () => {
    setSelectedWeekIndexes([]);
  };

  return (
    <Modal
      className="duplicate-meal-plan-week-modal"
      title={t("editMealPlan.duplicateMealPlanWeekModal.title", { date: dateToUse })}
    >
      <div className="body">
        <div>
          <div>
            <span className="text-sm">{t("editMealPlan.duplicateMealPlanWeekModal.duplicateTo")}</span>
          </div>
          <ul>
            {otherWeekIndexes.map((origIdx) => {
              const { start, end } = weeks[origIdx].rangeWeek;
              const label =
                start && end
                  ? getDateRangeLabel(start, end, origIdx + 1)
                  : `Week ${getDateRangeLabel(start, end, origIdx + 1)}`;

              const isSelected = selectedWeekIndexes.includes(origIdx);

              return (
                <li key={`${label}-${origIdx}`}>
                  <button
                    className="p-0"
                    type="button"
                    onClick={() => toggleWeekSelection(origIdx)}
                  >
                    <SvgIcon
                      src={`/img/new-icons/${
                        isSelected
                          ? "checked-square.svg"
                          : "unchecked-square.svg"
                      }`}
                      noFilterOnLight={isSelected}
                    />
                  </button>
                  <span className={!isSelected ? "opacity-70" : ""}>
                    {label}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
        <div className="footer">
          <button
            data-button-type="secondary"
            data-action="cancel"
            onClick={handleClearAll}
          >
            {t("editMealPlan.duplicateMealPlanWeekModal.clearAll")}
          </button>
          <button
            type="submit"
            data-button-type="primary"
            disabled={selectedWeekIndexes.length === 0}
            onClick={handleDuplicate}
          >
            {t("editMealPlan.duplicateMealPlanWeekModal.duplicate")}
          </button>
        </div>
      </div>
    </Modal>
  );
};

DuplicateMealPlanWeekModal.propTypes = {
  locals: PropTypes.object,
};
